import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Heart, Home, GraduationCap, ShieldCheck, PawPrint, Layers } from "lucide-react";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/seo";

const TOPICS = [
  { Icon: PawPrint, title: "What Is a Wolfdog?", body: "Understand wolf content, generations (F1, F2), and how hybrids differ from domestic dogs and pure wolves." },
  { Icon: Home, title: "Housing & Containment", body: "Enclosure standards, fencing, dig guards, and creating a safe, enriching environment." },
  { Icon: GraduationCap, title: "Behavior & Socialization", body: "Temperament ranges, early socialization, body language, and working with experienced handlers." },
  { Icon: Heart, title: "Health & Nutrition", body: "Diet considerations, veterinary care, vaccinations, and finding wolfdog-friendly vets." },
  { Icon: ShieldCheck, title: "Responsible Ownership", body: "Legal compliance, liability, insurance, and being a positive ambassador for the community." },
  { Icon: BookOpen, title: "Rescue & Rehoming", body: "How reputable rescues operate, the vetting process, and what adoption really involves." },
];

const CONTENT_LEVELS = [
  { key: "Low Content", label: "Low Content", range: "1% – 49% wolf", desc: "Microcontents, true lows & upper lows — the largest and most varied group." },
  { key: "Mid Content", label: "Mid Content", range: "50% – 75% wolf", desc: "The 'great paradox' — balanced instinct and domesticity for experienced owners." },
  { key: "High Content", label: "High Content", range: "85%+ grey wolf", desc: "Indistinguishable from a pure wolf — a full lifestyle commitment." },
];

export default function Information() {
  useSeo({ title: "Information Hub", description: "Educational resources about wolfdogs and responsible ownership." });
  const [articles, setArticles] = useState([]);
  const [guides, setGuides] = useState([]);

  useEffect(() => {
    api.get("/articles", { params: { status: "published", category: "Education", limit: 30 } })
      .then((r) => {
        const all = r.data;
        setGuides(all.filter((a) => a.title.startsWith("Understanding Wolf Content")));
        setArticles(all.filter((a) => !a.title.startsWith("Understanding Wolf Content")).slice(0, 6));
      }).catch(() => {});
  }, []);

  const guideFor = (key) => guides.find((g) => g.title.includes(key));

  return (
    <div className="max-w-7xl mx-auto px-4 py-14">
      <p className="overline text-[#8B6D1F]">Learn</p>
      <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight text-[#0A192F] mt-1">Information Hub</h1>
      <p className="mt-4 text-lg text-[#4A5568] max-w-2xl leading-relaxed">
        Everything you need to know about wolfdogs, ownership, and responsible care — curated by our editorial team.
      </p>

      {/* Wolf Content Guide */}
      <div className="mt-12 bg-[#0A192F] text-white p-8 sm:p-10 border-2 border-[#D4AF37]/40">
        <div className="flex items-center gap-3 mb-2">
          <Layers className="w-6 h-6 text-[#D4AF37]" />
          <h2 className="font-display text-2xl font-bold text-[#D4AF37]">Wolfdogs &amp; Grey Wolf Percentage</h2>
        </div>
        <p className="text-white/70 text-sm mb-8 max-w-3xl">
          Wolf content describes how much grey wolf DNA a wolfdog carries. Explore our in-depth guide to each part of the
          spectrum — behavior, appearance, containment, diet, and care.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {CONTENT_LEVELS.map((lvl) => {
            const g = guideFor(lvl.key);
            const inner = (
              <>
                <span className="overline text-[#D4AF37]">{lvl.range}</span>
                <h3 className="font-display text-xl font-bold text-white mt-1 group-hover:text-[#D4AF37] transition-colors">{lvl.label}</h3>
                <p className="mt-2 text-sm text-white/60 leading-relaxed">{lvl.desc}</p>
                {g && <span className="mt-4 inline-block overline text-[#D4AF37]">Read the Guide →</span>}
              </>
            );
            return g ? (
              <Link key={lvl.key} to={`/news/${g.slug}`} data-testid={`content-guide-${lvl.key.replace(/\s+/g, "-").toLowerCase()}`} className="group bg-white/5 border border-white/10 p-6 hover:border-[#D4AF37]/60 transition-colors">
                {inner}
              </Link>
            ) : (
              <div key={lvl.key} className="bg-white/5 border border-white/10 p-6">{inner}</div>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
        {TOPICS.map(({ Icon, title, body }) => (
          <div key={title} className="bg-white border border-[#0A192F]/10 p-7 hover:-translate-y-1 transition-transform">
            <div className="w-12 h-12 rounded-full bg-[#0A192F] flex items-center justify-center mb-4">
              <Icon className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <h3 className="font-display text-xl font-bold text-[#0A192F]">{title}</h3>
            <p className="mt-2 text-sm text-[#4A5568] leading-relaxed">{body}</p>
          </div>
        ))}
      </div>

      {articles.length > 0 && (
        <div className="mt-16">
          <h2 className="font-display text-2xl font-bold text-[#0A192F] mb-6">Educational Articles</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {articles.map((a) => (
              <Link key={a.id} to={`/news/${a.slug}`} className="group bg-white border border-[#0A192F]/10 p-6 hover:border-[#D4AF37] transition-colors">
                <span className="overline text-[#8B6D1F]">{a.category}</span>
                <h3 className="font-display text-lg font-bold text-[#0A192F] mt-2 group-hover:text-[#8B6D1F] transition-colors">{a.title}</h3>
                <p className="mt-2 text-sm text-[#4A5568] line-clamp-2">{a.excerpt}</p>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
